import { convertFromRaw, convertToRaw, EditorState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';

import { IcDeleteTrash, IcPencil } from '@/assets/images';
import { SpellCheckEditor } from '@/components/FormElements/FormTextEditorField';
import { FormTextEditorFieldRoot } from '@/components/FormElements/FormTextEditorField/styles';
import { Button, ConfirmationModal } from '@/components/TailwindComponents';
import { INarrative } from '@/models';
import { colors } from '@/theme/variables';
import { getFormattedDateTime } from '@/utils';
import {
  narrativeEntityToHtml,
  resolveNarrativeEntityMap,
} from '@/utils/narrative';

import type { useArrestNarratives } from '../hooks/useArrestNarratives';

interface NarrativeTabProps extends ReturnType<typeof useArrestNarratives> {
  narratives: INarrative[];
  isFormEditMode?: boolean;
  viewMode?: boolean;
  onCancelEdits?: () => void;
}

// Convert raw Draft.js content to HTML for display
const rawContentToHtml = (narrative: INarrative): string => {
  try {
    const content = narrative.content;
    if (!content?.text) return '';

    const entityMap = resolveNarrativeEntityMap(content);
    const fromRaw = convertFromRaw({
      ...content.text,
      entityMap,
    });

    return draftToHtml(
      convertToRaw(fromRaw),
      undefined,
      undefined,
      (entity, text) => narrativeEntityToHtml(entity, text),
    );
  } catch {
    // Fallback: extract plain text from blocks
    if (narrative.content?.text?.blocks) {
      return narrative.content.text.blocks
        .map((block: any) => block.text || '')
        .join('<br/>');
    }
    return '';
  }
};

// Parse narrative content into EditorState
export const narrativeToEditorState = (narrative: INarrative): EditorState => {
  try {
    if (!narrative.content?.text) return EditorState.createEmpty();
    const entityMap = resolveNarrativeEntityMap(narrative.content);
    const fromRaw = convertFromRaw({
      ...narrative.content.text,
      entityMap,
    });
    return EditorState.createWithContent(fromRaw);
  } catch {
    return EditorState.createEmpty();
  }
};

// Sort narratives: PRIMARY first, then by createdAt ascending
const sortNarratives = (narratives: INarrative[]): INarrative[] => {
  return [...narratives].sort((a, b) => {
    if (a.type === 'PRIMARY' && b.type !== 'PRIMARY') return -1;
    if (a.type !== 'PRIMARY' && b.type === 'PRIMARY') return 1;
    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return dateA - dateB;
  });
};

const NarrativeTab = ({
  narratives,
  isFormEditMode,
  editingIds,
  editorStates,
  newNarratives,
  isSaving,
  isDeleteModalOpen,
  isDeletingNarrative,
  isCreator,
  handleStartEdit,
  handleEditorChange,
  handleNewEditorChange,
  handleAddNew,
  handleStartEditNew,
  handleDeleteNew,
  resetAll,
  handleStandaloneSave,
  handleDeleteClick,
  handleConfirmDelete,
  handleCancelDelete,
  viewMode,
  onCancelEdits,
}: NarrativeTabProps) => {
  const sortedNarratives = sortNarratives(narratives);
  const canMutate = !viewMode;

  return (
    <div className="space-y-4 w-full">
      {/* Existing narrative cards */}
      {sortedNarratives.length > 0 ? (
        sortedNarratives.map((narrative) => {
          const narrativeId = narrative._id?.toString() || '';
          const canDelete = isCreator(narrative);
          const isEditing = editingIds.has(narrativeId);

          return (
            <div
              key={narrativeId}
              className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
            >
              {/* Narrative Header - hidden in edit mode */}
              {!isEditing && (
                <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {narrative.createdBy?.fullName ||
                          narrative.createdBy?.email ||
                          '-'}{' '}
                        &bull;{' '}
                        <span className="text-xs">
                          {narrative.createdAt
                            ? getFormattedDateTime(String(narrative.createdAt))
                            : '-'}
                        </span>{' '}
                        {narrative.updatedAt &&
                          narrative.updatedAt !== narrative.createdAt && (
                            <span className="text-xs">
                              &bull; Last updated:{' '}
                              {getFormattedDateTime(
                                String(narrative.updatedAt),
                              )}{' '}
                            </span>
                          )}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 ml-auto">
                      <button
                        type="button"
                        onClick={() => handleStartEdit(narrative)}
                        className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none"
                        title="Edit narrative"
                        disabled={!canMutate}
                      >
                        <IcPencil className="w-4 h-4" />
                      </button>
                      {canMutate && canDelete && (
                        <button
                          type="button"
                          onClick={() => handleDeleteClick(narrativeId)}
                          className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none"
                          title="Delete narrative"
                        >
                          <IcDeleteTrash
                            color={colors.error.main}
                            className="w-4 h-4"
                          />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Narrative Content - View or Edit */}
              {isEditing && editorStates[narrativeId] ? (
                <FormTextEditorFieldRoot flex={1}>
                  <SpellCheckEditor
                    editorState={editorStates[narrativeId]}
                    onEditorStateChange={(newState: EditorState) =>
                      handleEditorChange(narrativeId, newState)
                    }
                    editorStyle={{ minHeight: 125, fontSize: '16px' }}
                  />
                </FormTextEditorFieldRoot>
              ) : (
                <div
                  className={`px-4 py-3 ${
                    canMutate
                      ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50'
                      : ''
                  }`}
                  onClick={() => {
                    if (canMutate) handleStartEdit(narrative);
                  }}
                  title={canMutate ? 'Click to edit' : undefined}
                >
                  <div
                    className="text-sm text-gray-700 dark:text-gray-300 max-w-none break-words [&_p]:my-3 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_p:empty]:min-h-[1.25rem] [&_br]:leading-7"
                    dangerouslySetInnerHTML={{
                      __html: rawContentToHtml(narrative),
                    }}
                  />
                </div>
              )}
            </div>
          );
        })
      ) : newNarratives.length === 0 ? (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <p className="text-sm">
              No narratives have been added to this arrest yet.
            </p>
            <p className="text-xs mt-1 text-gray-400 dark:text-gray-500">
              Click &quot;Add New&quot; below to create the first narrative.
            </p>
          </div>
        </div>
      ) : null}

      {/* New (unsaved) narrative editors */}
      {newNarratives.map(({ tempId, editorState, isEditing }) => (
        <div
          key={tempId}
          className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden"
        >
          {/* Header with edit/delete icons — shown in view mode only */}
          {!isEditing && (
            <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-end gap-1">
                <button
                  type="button"
                  onClick={() => handleStartEditNew(tempId)}
                  className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none"
                  title="Edit narrative"
                  disabled={!canMutate}
                >
                  <IcPencil className="w-4 h-4" />
                </button>
                {canMutate && (
                  <button
                    type="button"
                    onClick={() => handleDeleteNew(tempId)}
                    className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none"
                    title="Delete narrative"
                  >
                    <IcDeleteTrash
                      color={colors.error.main}
                      className="w-4 h-4"
                    />
                  </button>
                )}
              </div>
            </div>
          )}

          {isEditing ? (
            <FormTextEditorFieldRoot flex={1}>
              <SpellCheckEditor
                editorState={editorState}
                onEditorStateChange={(newState: EditorState) =>
                  handleNewEditorChange(tempId, newState)
                }
                editorStyle={{ minHeight: 125, fontSize: '16px' }}
              />
            </FormTextEditorFieldRoot>
          ) : (
            <div
              className={`px-4 py-3 ${
                canMutate
                  ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50'
                  : ''
              }`}
              onClick={() => {
                if (canMutate) handleStartEditNew(tempId);
              }}
              title={canMutate ? 'Click to edit' : undefined}
            >
              <div
                className="text-sm text-gray-700 dark:text-gray-300 prose prose-sm dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{
                  __html: draftToHtml(
                    convertToRaw(editorState.getCurrentContent()),
                  ),
                }}
              />
            </div>
          )}
        </div>
      ))}

      {/* Actions row */}
      <div className="flex items-center justify-between">
        <Button
          type="button"
          variant="secondary"
          className="w-fit text-sm"
          onClick={handleAddNew}
          disabled={viewMode}
        >
          <span className="text-xl">+</span>
          Add new arrest narrative
        </Button>

        {/* Standalone Save/Cancel when parent form is not in edit mode */}
        {!isFormEditMode &&
          canMutate &&
          (editingIds.size > 0 || newNarratives.length > 0) && (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onCancelEdits ?? resetAll}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                type="button"
                color="error"
                onClick={handleStandaloneSave}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          )}
      </div>

      {/* Confirmation Modal for Deleting Narrative */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Narrative?"
        message="Are you sure you want to delete the narrative?"
        confirmButtonText="Yes, Delete it"
        confirmButtonColor="error"
        cancelButtonText="Cancel"
        isLoading={isDeletingNarrative}
      />
    </div>
  );
};

export default NarrativeTab;
